import React from 'react';
import { BarChart2, Trash, RefreshCw } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для сравнения сохраненных вкладов
 */
const ComparisonSidebar = ({ 
  savedDeposits, 
  deleteSavedDeposit, 
  loadSavedDeposit,
  currencyOptions
}) => {
  // Если нет сохраненных вкладов, показываем соответствующее сообщение
  if (!savedDeposits || savedDeposits.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-green-500" />
          Сравнение вкладов
        </h3>
        
        <div className="py-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            У вас пока нет сохраненных расчетов. 
            Нажмите "Сохранить расчет" после вычисления доходности.
          </p>
        </div>
      </div>
    );
  }
  
  // Получаем символ валюты
  const getCurrencySymbol = (currencyCode) => {
    const currency = currencyOptions.find(c => c.id === currencyCode);
    return currency ? currency.symbol : '';
  };

  // Функция форматирования денежных значений
  const formatMoney = (value, currencyCode) => {
    const symbol = getCurrencySymbol(currencyCode);
    return `${formatNumber(value, 2, '.', ' ')} ${symbol}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <BarChart2 className="w-5 h-5 mr-2 text-green-500" />
        Сравнение вкладов ({savedDeposits.length})
      </h3>
      
      <div className="space-y-3">
        {savedDeposits.map((deposit) => (
          <div 
            key={deposit.id}
            className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/70 transition-colors"
          >
            <div className="flex justify-between items-start">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  {deposit.name}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(deposit.date).toLocaleDateString('ru-RU')}
                </p>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => deleteSavedDeposit(deposit.id)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  aria-label="Удалить"
                  title="Удалить расчет"
                >
                  <Trash className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Сумма:</span>
                <span className="ml-1 font-medium text-gray-900 dark:text-white">
                  {formatMoney(parseFloat(deposit.formData.depositAmount), deposit.formData.currency)}
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Срок:</span>
                <span className="ml-1 font-medium text-gray-900 dark:text-white">
                  {deposit.formData.depositTerm} мес.
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Ставка:</span>
                <span className="ml-1 font-medium text-gray-900 dark:text-white">
                  {deposit.formData.interestRate}%
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Доход:</span>
                <span className="ml-1 font-medium text-green-600 dark:text-green-400">
                  +{formatMoney(deposit.results.netGain, deposit.formData.currency)}
                </span>
              </div>
            </div>
            
            <div className="mt-2">
              <Button
                onClick={() => loadSavedDeposit(deposit.id)}
                size="xs"
                variant="outline"
                icon={<RefreshCw className="w-3 h-3" />}
                className="w-full"
              >
                Загрузить
              </Button>
            </div>
          </div>
        ))}
      </div>
      
      {savedDeposits.length > 1 && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
          <p className="text-xs text-blue-600 dark:text-blue-400">
            <strong>Сравнение:</strong> Вклад с самой высокой доходностью — 
            {(() => {
              const maxProfitDeposit = [...savedDeposits].sort((a, b) => 
                b.results.effectiveRate - a.results.effectiveRate
              )[0];
              return maxProfitDeposit ? 
                ` "${maxProfitDeposit.name}" (${maxProfitDeposit.formData.interestRate}%)` : 
                ' не определен';
            })()}
          </p>
        </div>
      )}
    </div>
  );
};

export default ComparisonSidebar;